import { SearchFilters, EducationLevel, YearOfExperienceRange } from '../types';
import { getDomainLabel } from '../config/appConfig';

export const serializeFiltersToUrl = (filters: SearchFilters): string => {
  const params = new URLSearchParams();

  // Search Query
  if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
    params.set('q', filters.searchQuery.trim());
  }

  // Domain — always include, use display label for human-readable URLs
  if (filters.domain && filters.domain.length > 0) {
    params.set('domain', getDomainLabel(filters.domain));
  }

  // Page and pageSize
  if (filters.page && filters.page > 1) {
    params.set('page', filters.page.toString());
  }
  if (filters.pageSize && filters.pageSize !== 18) {
    params.set('pageSize', filters.pageSize.toString());
  }

  // Arrays
  if (filters.countries && filters.countries.length > 0) {
    params.set('countries', filters.countries.join(','));
  }

  if (filters.companies && filters.companies.length > 0) {
    params.set('companies', filters.companies.join(','));
  }

  if (filters.education && filters.education.length > 0) {
    params.set('education', filters.education.join(','));
  }

  if (filters.school && filters.school.trim().length > 0) {
    params.set('school', filters.school.trim());
  }

  if (filters.skills && filters.skills.length > 0) {
    const skillNames = filters.skills.map(s => (s.name || '').trim()).filter(Boolean);
    if (skillNames.length > 0) {
      params.set('skills', skillNames.join(','));
    }
  }

  if (filters.naturalLanguages && filters.naturalLanguages.length > 0) {
    params.set('naturalLanguages', filters.naturalLanguages.join(','));
  }

  if (filters.programmingLanguages && filters.programmingLanguages.length > 0) {
    params.set('programmingLanguages', filters.programmingLanguages.join(','));
  }

  // Year of Experience
  if (filters.yearOfExperience) {
    if (filters.yearOfExperience.min !== undefined) {
      params.set('yoeMin', filters.yearOfExperience.min.toString());
    }
    if (filters.yearOfExperience.max !== undefined) {
      params.set('yoeMax', filters.yearOfExperience.max.toString());
    }
  }

  // Hourly Rate
  if (filters.hourlyRate) {
    if (filters.hourlyRate.min !== undefined) {
      params.set('rateMin', filters.hourlyRate.min.toString());
    }
    if (filters.hourlyRate.max !== undefined) {
      params.set('rateMax', filters.hourlyRate.max.toString());
    }
  }

  // Certificates
  if (filters.certificates) {
    params.set('certificates', filters.certificates);
  }

  // Research Papers
  if (filters.researchPapersCount !== undefined) {
    params.set('researchPapersCount', filters.researchPapersCount.toString());
  }

  // Type of Work (Expertise)
  if (filters.typeOfWork && filters.typeOfWork.trim().length > 0) {
    params.set('typeOfWork', filters.typeOfWork.trim());
  }

  // Field (STEM)
  if (filters.field && filters.field.trim().length > 0) {
    params.set('field', filters.field.trim());
  }

  // Domain Filter (Finance)
  if (filters.domainFilter && filters.domainFilter.trim().length > 0) {
    params.set('domainFilter', filters.domainFilter.trim());
  }

  // Exclude filters
  if (filters.excludeViewed) {
    params.set('excludeViewed', '1');
  }
  if (filters.excludeSaved) {
    params.set('excludeSaved', '1');
  }
  if (filters.excludeDiscarded) {
    params.set('excludeDiscarded', '1');
  }

  // Inclusion filters (only viewed/saved/discarded)
  if (filters.onlyViewed) {
    params.set('onlyViewed', '1');
  }
  if (filters.onlySaved) {
    params.set('onlySaved', '1');
  }
  if (filters.onlyDiscarded) {
    params.set('onlyDiscarded', '1');
  }

  return params.toString();
};

export const deserializeFiltersFromUrl = (): SearchFilters => {
  const params = new URLSearchParams(window.location.search);
  const filters: SearchFilters = {
    page: 1,
    pageSize: 18,
  };

  // Search Query
  const searchQuery = params.get('q');
  if (searchQuery && searchQuery.trim().length > 0) {
    filters.searchQuery = searchQuery.trim();
  }

  // Domain
  const domain = params.get('domain');
  if (domain && domain.length > 0) {
    filters.domain = domain;
  }

  // Page and pageSize
  const page = params.get('page');
  if (page) {
    const pageNum = parseInt(page, 10);
    if (!isNaN(pageNum) && pageNum > 0) {
      filters.page = pageNum;
    }
  }

  const pageSize = params.get('pageSize');
  if (pageSize) {
    const pageSizeNum = parseInt(pageSize, 10);
    if (!isNaN(pageSizeNum) && pageSizeNum > 0) {
      filters.pageSize = pageSizeNum;
    }
  }

  // Arrays
  const countries = params.get('countries');
  if (countries) {
    filters.countries = countries.split(',').filter(Boolean);
  }

  const companies = params.get('companies');
  if (companies) {
    filters.companies = companies.split(',').filter(Boolean);
  }

  const education = params.get('education');
  if (education) {
    const eduArray = education.split(',').filter(Boolean);
    filters.education = eduArray.filter(d =>
      Object.values(EducationLevel).includes(d as EducationLevel)
    ) as EducationLevel[];
  }

  const school = params.get('school');
  if (school && school.trim().length > 0) {
    filters.school = school.trim();
  }

  const skillsParam = params.get('skills');
  if (skillsParam && skillsParam.trim().length > 0) {
    const names = skillsParam.split(',').map(n => n.trim()).filter(Boolean);
    if (names.length > 0) {
      filters.skills = names.map(name => ({ name }));
    }
  }

  const naturalLanguages = params.get('naturalLanguages');
  if (naturalLanguages) {
    filters.naturalLanguages = naturalLanguages.split(',').filter(Boolean);
  }

  const programmingLanguages = params.get('programmingLanguages');
  if (programmingLanguages) {
    filters.programmingLanguages = programmingLanguages.split(',').filter(Boolean);
  }

  // Year of Experience
  const yoeMin = params.get('yoeMin');
  const yoeMax = params.get('yoeMax');
  if (yoeMin || yoeMax) {
    const yearOfExperience: YearOfExperienceRange = {};
    if (yoeMin) {
      const min = parseInt(yoeMin, 10);
      if (!isNaN(min)) {
        yearOfExperience.min = min;
      }
    }
    if (yoeMax) {
      const max = parseInt(yoeMax, 10);
      if (!isNaN(max)) {
        yearOfExperience.max = max;
      }
    }
    if (yearOfExperience.min !== undefined || yearOfExperience.max !== undefined) {
      filters.yearOfExperience = yearOfExperience;
    }
  }

  // Hourly Rate
  const rateMin = params.get('rateMin');
  const rateMax = params.get('rateMax');
  if (rateMin || rateMax) {
    const hourlyRate: YearOfExperienceRange = {};
    if (rateMin) {
      const min = parseFloat(rateMin);
      if (!isNaN(min)) hourlyRate.min = min;
    }
    if (rateMax) {
      const max = parseFloat(rateMax);
      if (!isNaN(max)) hourlyRate.max = max;
    }
    if (hourlyRate.min !== undefined || hourlyRate.max !== undefined) {
      filters.hourlyRate = hourlyRate;
    }
  }

  // Certificates
  const certificates = params.get('certificates');
  if (certificates) {
    filters.certificates = certificates;
  }

  // Research Papers
  const researchPapersCount = params.get('researchPapersCount');
  if (researchPapersCount) {
    const count = parseInt(researchPapersCount, 10);
    if (!isNaN(count) && count >= 0) {
      filters.researchPapersCount = count;
    }
  }

  // Type of Work (Expertise)
  const typeOfWork = params.get('typeOfWork');
  if (typeOfWork && typeOfWork.trim().length > 0) {
    filters.typeOfWork = typeOfWork.trim();
  }

  // Field (STEM)
  const field = params.get('field');
  if (field && field.trim().length > 0) {
    filters.field = field.trim();
  }

  // Domain Filter (Finance)
  const domainFilter = params.get('domainFilter');
  if (domainFilter && domainFilter.trim().length > 0) {
    filters.domainFilter = domainFilter.trim();
  }

  // Exclude filters
  if (params.get('excludeViewed') === '1') {
    filters.excludeViewed = true;
  }
  if (params.get('excludeSaved') === '1') {
    filters.excludeSaved = true;
  }
  if (params.get('excludeDiscarded') === '1') {
    filters.excludeDiscarded = true;
  }

  if (params.get('onlyViewed') === '1') {
    filters.onlyViewed = true;
  }
  if (params.get('onlySaved') === '1') {
    filters.onlySaved = true;
  }
  if (params.get('onlyDiscarded') === '1') {
    filters.onlyDiscarded = true;
  }

  return filters;
};

export const updateUrlWithFilters = (filters: SearchFilters, replace: boolean = false) => {
  const queryString = serializeFiltersToUrl(filters);

  const currentParams = new URLSearchParams(window.location.search);
  const tab = currentParams.get('tab');

  const params = new URLSearchParams(queryString);
  if (tab) params.set('tab', tab);

  const finalQuery = params.toString();
  const newUrl = finalQuery
    ? `${window.location.pathname}?${finalQuery}`
    : window.location.pathname;

  if (replace) {
    window.history.replaceState({}, '', newUrl);
  } else {
    window.history.pushState({}, '', newUrl);
  }
};
